import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'
import { AUTH_SESSION_EXPIRED_EVENT, authService } from '../services/authService'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      if (window.location.pathname === '/verify-email') {
        setIsLoading(false)
        return
      }
      try {
        const isAuth = await authService.checkAuthStatus()
        setAuthenticated(isAuth)
        setUser(authService.getCurrentUser())
      } catch (error) {
        console.error('Auth check failed:', error)
        setAuthenticated(false)
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  useEffect(() => {
    const handleSessionExpired = () => {
      setAuthenticated(false)
      setUser(null)
      setIsLoading(false)
    }
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)
  }, [])

  const login = useCallback((userData) => {
    setAuthenticated(true)
    setUser(userData)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setAuthenticated(false)
      setUser(null)
    }
  }, [])

  const value = {
    authenticated,
    user,
    isLoading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
